import { useCallback } from 'react'
import { YStack, XStack, Text, Image, View, useMedia } from 'tamagui'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { faArrowLeftLong } from '@fortawesome/pro-light-svg-icons/faArrowLeftLong'
import FooterGraphicSvg from '@assets/product/footer_graphic_detail.svg'
import topBackground from '@assets/product/top_background.png'
import { useGetProductCoursePreviewCurriculum } from '@services/api/graphql/queries/getProductPage'
import { IProductCoursePreview, IProductVideoCover } from '@dtos/ProductPage'
import { CONTAINER_WIDTH } from './constants'
import { navigateTo } from './helpers'
import { useResolvedColors } from './hooks'
import { CoverMedia } from './CoverMedia'
import { CurriculumAccordion } from './CurriculumAccordion'
import { FooterCopyright } from './FooterSection'
import { HtmlContent } from './HtmlContent'
import { SchoolLogo } from './SchoolLogo'
import { SectionTitle } from './SectionTitle'

interface ICoursePreviewProps {
  preview: IProductCoursePreview
  productSlug: string
  schoolUrl: string
  videoCover: IProductVideoCover | null
}

// Preview de um curso do combo (/:slug/courses/:id) — réplica do
// course_preview.liquid do hero_theme: hero com link de volta pra página de
// vendas, sobre + conteúdo do curso e footer sem preço.
export function CoursePreview({
  preview,
  productSlug,
  schoolUrl,
  videoCover,
}: ICoursePreviewProps) {
  const { t } = useTranslation('ProductPage')
  const media = useMedia()
  const colors = useResolvedColors()
  const { getPreviewCurriculum } = useGetProductCoursePreviewCurriculum()

  const subtitle =
    preview.subtitle ||
    t(`courseType.${preview.kind ?? ''}`, { defaultValue: '' })

  const loadCurriculumPage = useCallback(
    async (page: number) => {
      const { data } = await getPreviewCurriculum({
        variables: {
          schoolUrl,
          slug: productSlug,
          courseId: preview.id,
          page,
        },
      })
      return data?.productPage?.coursePreview?.curriculum ?? null
    },
    [getPreviewCurriculum, schoolUrl, productSlug, preview.id],
  )

  return (
    <YStack backgroundColor="$bg-page" minHeight="100%" width="100%">
      <View width="100%" backgroundColor="$bg-surface" position="relative">
        {media.gtSm && (
          <Image
            source={topBackground}
            alt=""
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            width="45%"
            height="100%"
            resizeMode="contain"
            opacity={0.6}
            pointerEvents="none"
          />
        )}

        <YStack
          width="100%"
          maxWidth={CONTAINER_WIDTH}
          alignSelf="center"
          paddingVertical={64}
          paddingHorizontal={16}
        >
          <SchoolLogo centered={!media.gtSm} />

          <XStack
            alignItems="center"
            gap={8}
            marginBottom={32}
            alignSelf={media.gtSm ? 'flex-start' : 'center'}
            cursor="pointer"
            hoverStyle={{ opacity: 0.8 }}
            onPress={() => navigateTo(`/${productSlug}`)}
            testID="course-preview-back"
          >
            <FontAwesomeIcon
              icon={faArrowLeftLong}
              size={14}
              color={colors.accent}
            />
            <Text
              color="$accent"
              fontSize={16}
              fontFamily="$mulishBold"
              fontWeight="700"
            >
              {t('coursePreview.go-back')}
            </Text>
          </XStack>

          <XStack
            width="100%"
            gap={48}
            alignItems={media.gtSm ? 'flex-start' : 'center'}
            flexDirection={media.gtSm ? 'row' : 'column'}
          >
            <YStack
              flex={media.gtSm ? 1 : undefined}
              width={media.gtSm ? undefined : '100%'}
              gap={12}
            >
              <Text
                tag="h1"
                color="$text-primary"
                fontSize={media.gtSm ? 48 : 32}
                lineHeight={media.gtSm ? 56 : 40}
                fontFamily="$mulishBold"
                fontWeight="700"
                textAlign={media.gtSm ? 'left' : 'center'}
              >
                {preview.name}
              </Text>
              {subtitle ? (
                <Text
                  color="$text-primary"
                  fontSize={20}
                  fontFamily="$mulishSemiBold"
                  fontWeight="600"
                  textAlign={media.gtSm ? 'left' : 'center'}
                >
                  {subtitle}
                </Text>
              ) : null}
            </YStack>

            <CoverMedia
              videoCover={videoCover}
              imageUrl={preview.imageUrl}
              title={preview.name}
            />
          </XStack>
        </YStack>
      </View>

      <YStack
        width="100%"
        maxWidth={CONTAINER_WIDTH}
        alignSelf="center"
        paddingHorizontal={16}
        marginTop={64}
        flexGrow={1}
      >
        {preview.description ? (
          <YStack marginBottom={64} testID="course-preview-description">
            <SectionTitle>{t('about.title')}</SectionTitle>
            <HtmlContent html={preview.description} fullWidth />
          </YStack>
        ) : null}

        {preview.kind !== 'ebook' && preview.curriculum.nodes.length > 0 && (
          <YStack width="100%">
            <SectionTitle>{t('content.title')}</SectionTitle>
            <CurriculumAccordion
              initialPage={preview.curriculum}
              onLoadMore={loadCurriculumPage}
            />
          </YStack>
        )}
      </YStack>

      <View width="100%" backgroundColor="$bg-surface" marginTop={64}>
        <YStack
          width="100%"
          maxWidth={CONTAINER_WIDTH}
          alignSelf="center"
          alignItems="center"
          paddingTop={0}
          paddingBottom={48}
          paddingHorizontal={16}
          gap={24}
        >
          <FooterGraphicSvg width={115} height={61} />
          <FooterCopyright />
        </YStack>
      </View>
    </YStack>
  )
}
